import BitmapFontTextView, {
  BitmapFontTextViewOpts,
} from '@play-co/timestep-core/lib/ui/bitmapFont/BitmapFontTextView';
import { bitmapFontFromFontID } from 'src/lib/bitmapFonts';
import { createPersistentEmitter, Emitter, Listener } from 'src/lib/Emitter';
import { FontName } from 'src/lib/ui/config';

let emitter: Emitter<string>;

/**
 * Enable or disable auto font switch, based on current font size.
 * If higher than this value, it will try to switch to the "Big" version of the font.
 * If lower than this value, it will use the regular font.
 * For the upscale, it can be set to 36, since it is 0.75 of the regular font size.
 * Set to 0 to disable.
 */
const AUTO_SWITCH_FONT_SIZE = 36;

// `font` parameter of the LangBitmapFontTextView component is `FontName`
export type LangBitmapOpts = Omit<BitmapFontTextViewOpts, 'font'> & {
  font?: FontName;
  localeText?: () => string;
};

export default class LangBitmapFontTextView extends BitmapFontTextView {
  public get characters() {
    return this._characters;
  }

  private fontID: FontName | null = null;
  private localizedText: (() => string) | null = null;

  public set localeText(text: (() => string) | null) {
    this.updateFontAndText(this.fontID, text);
  }

  public get localeText() {
    return this.localizedText;
  }

  public notifyLocaleTextChanged() {
    this.updateFontAndText(this.fontID, this.localizedText);
  }

  constructor(opts: LangBitmapOpts) {
    super({ ...opts, font: undefined });
    this.updateFontAndText(opts.font, opts.localeText);

    if (!emitter) {
      emitter = createPersistentEmitter(({ ui }) => ui.locale);
    }

    let listener: Listener<any> | null = null;
    if (this.getSuperview()) {
      listener = emitter.addListener(() => {
        this.updateFontAndText(this.fontID, this.localizedText);
      });
    }

    this.on('ViewAdded', () => {
      if (!listener) {
        listener = emitter.addListener(() => {
          this.updateFontAndText(this.fontID, this.localizedText);
        });
      }
    });

    this.on('ViewRemoved', () => {
      if (listener) {
        emitter.removeListener(listener);
        listener = null;
      }
    });
  }

  private updateFontAndText(
    font: FontName | null | undefined,
    localeText: (() => string) | null | undefined,
  ) {
    if (font) {
      this.fontID = this.getFontBySize(font, this.size);
    } else if (font === null) {
      this.fontID = null;
    }
    if (this.fontID && typeof this.fontID === 'object') {
      throw Error('FontID was assigned an object instead of a FontName');
    }

    if (localeText) {
      this.localizedText = localeText;
    } else if (localeText === null) {
      this.localizedText = null;
    }

    const trueFont = this.fontID ? bitmapFontFromFontID(this.fontID) : null;
    if (this.fontID && !trueFont) {
      console.error(`Font ${this.fontID} does not exist`);
    }

    this.font = trueFont;
    this.text = this.localizedText ? this.localizedText() : '';
  }

  public updateOpts(options: LangBitmapOpts) {
    const baseCompatibleOpts = {
      ...options,
      font: undefined,
      text: undefined,
    };
    super.updateOpts(baseCompatibleOpts);
    this.updateFontAndText(options.font, options.localeText);
    return baseCompatibleOpts;
  }

  private getFontBySize(font: FontName, size: number) {
    // Just return the same font if the auto switch is disabled
    if (!AUTO_SWITCH_FONT_SIZE) return font;

    // Check if it is a big font already
    const isBigFont = font.endsWith('Big');
    let newFontName = font;

    if (isBigFont && size <= AUTO_SWITCH_FONT_SIZE) {
      // Use the regular version if the size is smaller than the threshold
      newFontName = font.replace('Big', '') as FontName;
    } else if (!isBigFont && size > AUTO_SWITCH_FONT_SIZE) {
      // Use the big version if the size is bigger than the threshold
      newFontName = (font + 'Big') as FontName;
    }

    // Check if the font exists
    const cached = bitmapFontFromFontID(newFontName);

    // Return the new font if it exists, otherwise return the original font
    return cached ? newFontName : font;
  }
}
